import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Tag,
  DatePicker,
  Input,
  Space,
  Button,
  Pagination,
  Row,
  Modal,
  Alert,
  Checkbox,
  Tooltip,
  message,
} from "antd";
import axios from "axios";
import PropTypes from "prop-types";
import { formatDateTime } from "../../helpers/date";
import { formatAmount } from "../../helpers/billingStatement";
import { BILLING_TRANSACTION_STATUS_COLORS } from "../../constants/billingTransaction";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { RangePicker } = DatePicker;

const STATUS_CONFIRMED = "CONFIRMED";
const STATUS_PENDING = "PENDING";
const STATUS_FAILED = "FAILED";
const REASON_MIN = 3;
const REASON_MAX = 200;

// The only 409 that can be overridden, and only by Finance confirming no payment was sent. The
// other 409s (not pending, already succeeded, still in flight at the gateway) have no override.
const isNoResultNoReference = (error) =>
  error.response?.status === 409 && /no result and no gateway reference/i.test(error.response?.data?.message || "");

// Rows are the admin listing's raw transactions, so the id is objectId (not the customer view's id).
const rowId = (record) => record.objectId || record.id;
// COD offsets, every status: PENDING and FAILED are visible here (unlike the
// merchant's own view) so Finance can see a payout that is still in flight or did not go through.
// With a merchantId it is that merchant's list (User Details > Billing); without one it is every
// merchant's, with a Merchant column (Finance > Billing > Contra Report).
const UserBillingCodOffsets = ({ merchantId }) => {
  const { permissions } = usePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState(null);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20 });

  // Release dialog: the offset being released, the typed reason, the last server error, and the
  // confirm-not-paid step that only the "no result and no gateway reference" 409 reveals
  const [releasing, setReleasing] = useState(null);
  const [reason, setReason] = useState("");
  const [releaseError, setReleaseError] = useState(null);
  const [needsNotPaidConfirm, setNeedsNotPaidConfirm] = useState(false);
  const [notPaidConfirmed, setNotPaidConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Only the latest request may update the list, so a slow earlier response can't overwrite it
  const latestRequest = useRef(0);

  const fetchItems = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const res = await axios.get("/api/cod-offsets", {
        params: {
          ...(merchantId && { merchantId }),
          page: pagination.currentPage,
          limit: pagination.limit,
          ...(range && { from: range[0].format("YYYY-MM-DD"), to: range[1].format("YYYY-MM-DD") }),
          ...(search && { search }),
        },
      });
      if (requestId !== latestRequest.current) return;
      setItems(res.data.data);
      setPagination((prev) => ({ ...prev, totalSize: res.data.pagination?.totalSize }));
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      message.error(error.response?.data?.message || error.message);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [merchantId, pagination.currentPage, pagination.limit, range, search]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const backToFirstPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  // The API answers 409 until the payout is CONFIRMED, so the buttons are disabled before then
  const downloadReport = (offsetId, kind) => {
    axios
      .get(`/api/cod-offsets/${offsetId}/${kind}`)
      .then((res) => window.open(res.data.url))
      .catch((error) => message.error(error.response?.data?.message || error.message));
  };

  const openRelease = (record) => {
    setReleasing(record);
    setReason("");
    setReleaseError(null);
    setNeedsNotPaidConfirm(false);
    setNotPaidConfirmed(false);
  };

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;

  const handleRelease = async () => {
    if (!reasonValid || (needsNotPaidConfirm && !notPaidConfirmed)) return;

    setSubmitting(true);
    setReleaseError(null);
    try {
      await axios.post(`/api/cod-offsets/${rowId(releasing)}/release`, {
        reason: trimmedReason,
        // Only after that specific 409 was shown and the box ticked, never on the first attempt
        ...(needsNotPaidConfirm && notPaidConfirmed && { confirmNotPaid: true }),
      });
      message.success("COD offset released");
      setReleasing(null);
      fetchItems();
    } catch (error) {
      // Any other error (including "still in flight") takes the override away again
      setNeedsNotPaidConfirm(isNoResultNoReference(error));
      setNotPaidConfirmed(false);
      setReleaseError(error.response?.data?.message || error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "Offset No.", dataIndex: "offsetNumber", key: "offsetNumber", render: (n) => n || "-" },
    ...(merchantId ? [] : [{ title: "Merchant", dataIndex: "userEmail", key: "userEmail" }]),
    { title: "Billing Period", dataIndex: "billingPeriod", key: "billingPeriod" },
    {
      title: "Processed",
      dataIndex: "processedAt",
      key: "processedAt",
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    { title: "COD Amount", key: "codAmount", align: "right", render: (_, r) => formatAmount(r.codAmount, "MYR") },
    { title: "Total Offset", key: "amount", align: "right", render: (_, r) => formatAmount(r.amount, "MYR") },
    {
      title: "Paid to Merchant",
      key: "payoutAmount",
      align: "right",
      // Nothing was paid on PENDING or FAILED rows; the PDF prefers grandTotal, so do the same
      render: (_, r) =>
        r.status === STATUS_CONFIRMED ? formatAmount(r.grandTotal ?? r.payoutAmount, "MYR") : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const tag = <Tag color={BILLING_TRANSACTION_STATUS_COLORS[status]}>{status}</Tag>;
        return status === STATUS_FAILED && record.failureReason ? (
          <Tooltip title={record.failureReason}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: "Failure Reason",
      dataIndex: "failureReason",
      key: "failureReason",
      ellipsis: true,
      render: (reason) => reason || "-",
    },
    { title: "Remittance Ref.", dataIndex: "remittanceReference", key: "remittanceReference" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            disabled={record.status !== STATUS_CONFIRMED}
            onClick={() => downloadReport(rowId(record), "pdf")}
          >
            PDF
          </Button>
          <Button
            type="link"
            size="small"
            disabled={record.status !== STATUS_CONFIRMED}
            onClick={() => downloadReport(rowId(record), "excel")}
          >
            Excel
          </Button>
          {(record.status === STATUS_PENDING || record.status === STATUS_FAILED) &&
            permissions?.includes(PERMISSIONS.RELEASE_COD_OFFSET) && (
            <Button type="link" size="small" danger onClick={() => openRelease(record)}>
              Release
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            value={range}
            onChange={(value) => {
              setRange(value && value[0] && value[1] ? value : null);
              backToFirstPage();
            }}
          />
          <Input.Search
            allowClear
            placeholder="Offset number"
            style={{ width: 220 }}
            onSearch={(value) => {
              setSearch(value.trim());
              backToFirstPage();
            }}
          />
        </Space>
      </Row>

      <Table
        rowKey={rowId}
        columns={columns}
        dataSource={items}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
        locale={{ emptyText: "No COD offsets yet" }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Pagination
          total={pagination.totalSize}
          pageSize={pagination.limit}
          current={pagination.currentPage}
          showSizeChanger
          onChange={(currentPage, limit) => setPagination((prev) => ({ ...prev, currentPage, limit }))}
        />
      </Row>

      <Modal
        title={`Release COD offset ${releasing?.offsetNumber || ""}`.trim()}
        visible={!!releasing}
        onCancel={() => setReleasing(null)}
        onOk={handleRelease}
        okText="Release"
        okButtonProps={{ danger: true, disabled: !reasonValid || (needsNotPaidConfirm && !notPaidConfirmed) }}
        confirmLoading={submitting}
        maskClosable={false}
      >
        {releasing?.status === STATUS_FAILED ? (
          <>
            <p>
              This offset is already FAILED. Releasing resumes a manual release that did not finish, and frees
              any charges still held. Only do this if the payout did not go through.
            </p>
            {releasing.failureReason && (
              <p>
                Failure reason: <strong>{releasing.failureReason}</strong>
              </p>
            )}
          </>
        ) : (
          <p>
            This frees the charges for this offset and marks it FAILED. Only do this if the payout did not go
            through.
          </p>
        )}
        <p>
          Payout reference: <strong>{releasing?.remittanceReference || "-"}</strong>
        </p>

        <div>
          Reason <span style={{ color: "red" }}>*</span>
        </div>
        <Input.TextArea
          rows={3}
          maxLength={REASON_MAX}
          showCount
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={`${REASON_MIN} to ${REASON_MAX} characters`}
        />
        {reason.length > 0 && !reasonValid && (
          <div style={{ color: "red" }}>Reason must be {REASON_MIN} to {REASON_MAX} characters.</div>
        )}

        {releaseError && <Alert className="mt-2" type="error" showIcon message={releaseError} />}

        {needsNotPaidConfirm && (
          <Checkbox
            className="mt-2"
            checked={notPaidConfirmed}
            onChange={(e) => setNotPaidConfirmed(e.target.checked)}
          >
            I confirm that no payment was sent for this payout
          </Checkbox>
        )}
      </Modal>
    </div>
  );
};

UserBillingCodOffsets.propTypes = {
  merchantId: PropTypes.string,
};

export default UserBillingCodOffsets;
